import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, MessageSquare, Search, Square, Volume2, X } from 'lucide-react';

function HistoryPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [selectedUser, setSelectedUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);

  const [playingAudioIndex, setPlayingAudioIndex] = useState(null);
  const audioRefs = useRef({});

  const stopAllAudio = () => {
    Object.keys(audioRefs.current).forEach((key) => {
      audioRefs.current[key].pause();
      audioRefs.current[key].currentTime = 0;
    });
    setPlayingAudioIndex(null);
  };

  const fetchUsers = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/datatable/users`, {
      params: {
        role: 'USER',
        start: page * limit,
        length: limit,
        search,
      },
      withCredentials: true,
    });

    setUsers(res.data.data);
    setTotal(res.data.recordsFiltered);
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search, limit]);

  const viewSessions = async (user) => {
    stopAllAudio();
    setSelectedUser(user);

    const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/chat-sessions/${user.id}`, {
      withCredentials: true,
    });

    setSessions(res.data);
    setMessages([]);
  };

  const viewMessages = async (sessionId) => {
    stopAllAudio();

    const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/chat-messages/${sessionId}`, {
      withCredentials: true,
    });

    setMessages(res.data);
  };

  return (
    <div className="tw w-full px-2">

      {!selectedUser ? (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-slate-800">
              <MessageSquare size={18} />
              <h4 className="mb-0 text-lg font-semibold">Data History Chat</h4>
            </div>

            <div className="mb-4 d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">Tampilkan</span>
                <select
                  className="form-select form-select-sm rounded-lg border-slate-300"
                  style={{ width: '88px' }}
                  value={limit}
                  onChange={(e) => {
                    setPage(0);
                    setLimit(Number(e.target.value));
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="small text-muted">data</span>
              </div>

              <div className="min-w-[280px] max-w-[360px] flex-1">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="Cari nama atau email user..."
                    value={search}
                    onChange={(e) => {
                      setPage(0);
                      setSearch(e.target.value);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-300 bg-white">
            <table className="w-full border-collapse text-left text-sm border border-slate-300">
              <thead>
                <tr className="bg-slate-50 text-slate-600">
                  <th className="border-b border-slate-200 px-3 py-2">Nama</th>
                  <th className="border-b border-slate-200 px-3 py-2">Email</th>
                  <th className="border-b border-slate-200 px-3 py-2 text-center">Paket</th>
                  <th className="border-b border-slate-200 px-3 py-2 text-center">Attempts</th>
                  <th className="border-b border-slate-200 px-3 py-2">Last Login</th>
                  <th className="border-b border-slate-200 px-3 py-2 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {users.map((item) => (
                  <tr key={item.id} className="odd:bg-white even:bg-slate-50 hover:bg-slate-100">
                    <td className="border border-slate-200 px-3 py-2">{item.name}</td>
                    <td className="border border-slate-200 px-3 py-2">{item.email}</td>
                    <td className="border border-slate-200 px-3 py-2 text-center font-semibold">{item.price_name}</td>
                    <td className="border border-slate-200 px-3 py-2 text-center font-semibold">{item.attempts}</td>
                    <td className="border border-slate-200 px-3 py-2">{new Date(item.last_login).toLocaleString()}</td>
                    <td className="border border-slate-200 px-3 py-2 text-center">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                        onClick={() => viewSessions(item)}
                      >
                        <Eye size={13} />
                        Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div className="mt-3 d-flex justify-content-start align-items-center gap-3 text-sm text-slate-500">
              <small>Total: {total}</small>
              <div className="d-inline-flex align-items-center gap-2 rounded-pill border border-slate-200 bg-white px-2 py-1 shadow-sm">
                <button
                  type="button"
                  className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center disabled:opacity-40"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                  aria-label="Halaman sebelumnya"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="px-2 small text-muted">{page + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center disabled:opacity-40"
                  disabled={(page + 1) * limit >= total}
                  onClick={() => setPage(page + 1)}
                  aria-label="Halaman berikutnya"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setSelectedUser(null);
                setSessions([]);
                setMessages([]);
                stopAllAudio();
              }}
            >
              <ArrowLeft size={14} />
              Kembali
            </button>
            <h5 className="mb-0 text-lg font-semibold text-slate-800">Chat Sessions for {selectedUser.name}</h5>
          </div>

          {sessions.length > 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <ul className="space-y-2">
                {sessions.map((s) => (
                  <li
                    key={s.sessionId}
                    className="flex flex-col gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm md:flex-row md:items-center md:justify-between"
                  >
                    <span>
                      {s.session_name} - {new Date(s.created_at).toLocaleString()}
                    </span>
                    <button
                      type="button"
                      className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                      onClick={() => viewMessages(s.sessionId)}
                    >
                      Lihat Chat
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="rounded-xl border border-slate-200 bg-white p-4 text-center text-sm text-slate-500">
              Tidak ada history chat yang tersedia.
            </p>
          )}
        </div>
      )}

      {messages.length > 0 ? (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <span className="text-sm font-semibold text-slate-700">Detail Chat</span>
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white p-1.5 text-slate-600 hover:bg-slate-50"
              onClick={() => {
                setMessages([]);
                stopAllAudio();
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto p-4">
            {messages.map((msg, idx) => (
              <div key={`${msg.role}-${idx}`} className="mb-4 border-b border-slate-100 pb-3 last:mb-0 last:border-b-0">
                <strong className={msg.role === 'user' ? 'text-slate-800' : 'text-slate-500'}>
                  {msg.role === 'user' ? selectedUser.name : 'Bot'}:
                </strong>

                <div className="mt-1 text-sm text-slate-700">
                  {msg.type === 'text' ? msg.message : null}
                  {msg.type === 'audio' ? (
                    <div>
                      <div>{msg.message}</div>
                      {msg.audioUrl ? (
                        <button
                          type="button"
                          className={`mt-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ${
                            msg.role === 'user'
                              ? 'border border-slate-300 bg-white text-slate-700'
                              : 'bg-slate-900 text-white'
                          }`}
                          onClick={() => {
                            const audioKey = `${msg.role}-${idx}`;
                            const currentAudio = audioRefs.current[audioKey];

                            if (currentAudio) {
                              if (!currentAudio.paused) {
                                currentAudio.pause();
                                currentAudio.currentTime = 0;
                                setPlayingAudioIndex(null);
                              } else {
                                stopAllAudio();
                                currentAudio.play();
                                setPlayingAudioIndex(idx);
                              }
                            } else {
                              const newAudio = new Audio(`${process.env.REACT_APP_API_BASE_URL}/${msg.audioUrl}`);
                              stopAllAudio();
                              audioRefs.current[audioKey] = newAudio;
                              newAudio.play();
                              setPlayingAudioIndex(idx);
                              newAudio.onended = () => setPlayingAudioIndex(null);
                            }
                          }}
                        >
                          {playingAudioIndex === idx ? <Square size={12} /> : <Volume2 size={12} />}
                          {playingAudioIndex === idx ? 'Stop' : 'Play Audio'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {msg.exportUrl ? (
                    <div className="mt-2">
                      <a
                        href={`${process.env.REACT_APP_API_BASE_URL}/chat/download/${msg.exportUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Unduh {msg.exportUrl.endsWith('.pdf') ? 'PDF' : 'DOCX'}
                      </a>
                    </div>
                  ) : null}
                </div>

                <small className="mt-2 block text-xs text-slate-400">{new Date(msg.created_at).toLocaleString()}</small>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default HistoryPage;
